import React, { useState } from 'react';
import { ArrowLeft, Package, TrendingUp, TrendingDown, Fuel, Bomb, FlaskConical, Circle, Wrench, Droplets, AlertTriangle, CheckCircle, Target, X, ChevronRight, ChevronDown, Shield, Hammer, ArrowUpRight, ArrowDownRight, Minus, Info, DollarSign, BarChart3 } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  LineChart, Line, ComposedChart, Cell, Legend, Area, AreaChart
} from 'recharts';
import { mockData } from '../../data/mockData';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import ExecutiveDrilldownCard from '../../components/ExecutiveDrilldownCard';
import { 
  getCurrentPeriodLabel, 
  getPreviousPeriodLabel, 
  getBudgetLabel,
  getTitleSuffix,
  getComparisonSubtitle
} from '../../utils/timeLabels';

const COLORS = ['#EF4444', '#6366F1', '#F59E0B', '#DC2626', '#0EA5E9', '#10B981', '#8B5CF6', '#14B8A6', '#64748B'];

// Custom tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-primary text-white rounded-lg px-4 py-3 shadow-xl">
        <p className="text-sm font-medium mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color === '#0A192F' ? '#C5A059' : entry.color }}>
            {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// ============ SEMANTIC COLOR SYSTEM - SCORECARD PATTERN ============
const statusColors = {
  positive: '#0C8A4B',
  positiveBg: '#EAF7EF',
  warning: '#A07A18',
  warningBg: '#F8F1DD',
  negative: '#C33D52',
  negativeBg: '#FBECEF',
};

const getSemanticBadgeStyle = (val, inverse = false, useWarning = true) => {
  if (val === undefined || val === null) return { bg: 'bg-slate-100', text: 'text-slate-600' };
  const isFavorable = inverse ? val <= 0 : val >= 0;
  const absVal = Math.abs(val);
  if (useWarning && !isFavorable && absVal <= 5) {
    return { bg: 'bg-amber-50', text: 'text-amber-700', bgColor: statusColors.warningBg, color: statusColors.warning };
  }
  if (isFavorable) {
    return { bg: 'bg-emerald-100', text: 'text-emerald-700', bgColor: statusColors.positiveBg, color: statusColors.positive };
  }
  return { bg: 'bg-rose-100', text: 'text-rose-700', bgColor: statusColors.negativeBg, color: statusColors.negative };
};

const getTrendIconInfo = (vsBudget, vsYoY, inverse = false) => {
  const mainVal = vsBudget !== undefined ? vsBudget : vsYoY;
  if (mainVal === undefined) return null;
  const isFavorable = inverse ? mainVal <= 0 : mainVal >= 0;
  const isUp = mainVal > 0;
  return {
    isUp,
    bgColor: isFavorable ? statusColors.positiveBg : statusColors.negativeBg,
    iconColor: isFavorable ? statusColors.positive : statusColors.negative,
  };
};

// KPI Card - SCORECARD EXECUTIVO PATTERN (inverse=true for cost metrics)
const KPICard = ({ title, value, unit, change, changeUnit = '%', isPositive, subtitle, icon: Icon, ppChange, vsBudget, budgetValue, language = 'pt', useWarning = true }) => {
  // For cost metrics, lower is better (inverse=true)
  const inverse = true;
  
  // Get semantic styles
  const budgetStyle = getSemanticBadgeStyle(vsBudget, inverse, useWarning);
  const yoyStyle = getSemanticBadgeStyle(change, inverse, useWarning);
  const trendInfo = getTrendIconInfo(vsBudget, change, inverse);
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-gold-300/50 transition-all duration-300" data-testid={`kpi-${title.toLowerCase().replace(/\s/g, '-')}`}>
      {/* SCORECARD LAYOUT: Content on left, Trend Icon on right */}
      <div className="flex items-start justify-between">
        {/* Left side: Title, Value, Badges */}
        <div className="flex-1 min-w-0">
          {/* Title - Normal case, not uppercase */}
          <p className="text-xs text-slate-500 tracking-wide mb-1.5">{title}</p>
          
          {/* Value + Unit - Smaller size to match Personnel Costs */}
          <div className="flex items-baseline gap-1.5 mb-2">
            <span className="font-sans text-2xl font-semibold tracking-[-0.3px] text-primary">
              {value}
            </span>
            <span className="text-sm text-slate-400">{unit}</span>
          </div>
          
          {subtitle && <p className="text-xs text-slate-400 mb-2">{subtitle}</p>}
          
          {/* Comparison Badges - Inline compact style with no wrap */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {vsBudget !== undefined && vsBudget !== null && (
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${budgetStyle.bg} ${budgetStyle.text}`}>
                {parseFloat(vsBudget) <= 0 ? <TrendingDown className="w-2.5 h-2.5" /> : <TrendingUp className="w-2.5 h-2.5" />}
                {parseFloat(vsBudget) > 0 ? '+' : ''}{vsBudget}% Orç.
              </span>
            )}
            {change !== undefined && (
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${yoyStyle.bg} ${yoyStyle.text}`}>
                {parseFloat(change) <= 0 ? <TrendingDown className="w-2.5 h-2.5" /> : <TrendingUp className="w-2.5 h-2.5" />}
                {parseFloat(change) > 0 ? '+' : ''}{change}{changeUnit} YoY
              </span>
            )}
            {ppChange !== undefined && ppChange !== null && (
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${
                parseFloat(ppChange) <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-50 text-amber-700'
              }`}>
                {parseFloat(ppChange) > 0 ? '+' : ''}{ppChange} pp
              </span>
            )}
          </div>
        </div>
        
        {/* Right side: Circular Trend Icon - SCORECARD PATTERN */}
        {trendInfo && (
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ml-3"
            style={{ backgroundColor: trendInfo.bgColor }}
          >
            {trendInfo.isUp ? (
              <ArrowUpRight className="w-5 h-5" style={{ color: trendInfo.iconColor }} />
            ) : (
              <ArrowDownRight className="w-5 h-5" style={{ color: trendInfo.iconColor }} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Waterfall Chart Component - Interactive with Driver Decomposition
const InteractiveWaterfallChart = ({ data, language, onDriverClick }) => {
  // Process data for waterfall visualization
  let cumulative = 0;
  const processedData = data.map((item, index) => {
    const displayName = typeof item.name === 'object' ? item.name[language] : item.name;
    if (item.type === 'start') {
      cumulative = item.value;
      return { ...item, displayName, start: 0, end: item.value, isPositive: true, isTotal: true, index, originalValue: item.value };
    } else if (item.type === 'end') {
      return { ...item, displayName, start: 0, end: item.value, isPositive: true, isTotal: true, index, originalValue: item.value };
    } else {
      const start = cumulative;
      cumulative += item.value;
      return { 
        ...item, 
        displayName, 
        start: Math.min(start, cumulative), 
        end: Math.max(start, cumulative),
        barValue: Math.abs(item.value),
        isPositive: item.value < 0 || item.type === 'decrease',
        index,
        isClickable: !!item.decomposition,
        originalValue: item.value
      };
    }
  });

  const handleBarClick = (barData, index) => {
    const originalData = processedData[index];
    if (originalData && originalData.decomposition) {
      onDriverClick(originalData);
    }
  };

  return (
    <ResponsiveContainer width="100%" height={320}>
      <ComposedChart data={processedData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
        <XAxis 
          dataKey="displayName" 
          tick={{ fontSize: 9, fill: '#64748B' }} 
          interval={0}
          angle={-45}
          textAnchor="end"
          height={80}
        />
        <YAxis tick={{ fontSize: 10 }} stroke="#94A3B8" domain={['dataMin - 5', 'dataMax + 5']} />
        <Tooltip 
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              const item = payload[0].payload;
              const description = item.description ? (typeof item.description === 'object' ? item.description[language] : item.description) : null;
              return (
                <div className="bg-slate-800 text-white px-3 py-2 rounded-lg shadow-lg text-sm">
                  <p className="font-medium">{item.displayName}</p>
                  <p className={item.isPositive ? 'text-emerald-400' : 'text-red-400'}>
                    {item.type === 'start' || item.type === 'end' ? '' : (item.value > 0 ? '+' : '')}{item.value}M USD
                  </p>
                  {description && <p className="text-slate-300 text-xs mt-1">{description}</p>}
                  {item.isClickable && (
                    <p className="text-gold-400 text-xs mt-2 flex items-center gap-1">
                      <ChevronRight className="w-3 h-3" />
                      {language === 'pt' ? 'Clique para ver decomposição' : 'Click for decomposition'}
                    </p>
                  )}
                </div>
              );
            }
            return null;
          }}
        />
        <Bar 
          dataKey="end" 
          radius={[4, 4, 0, 0]}
          onClick={(data, index) => handleBarClick(data, index)}
          cursor="pointer"
        >
          {processedData.map((entry, index) => (
            <Cell 
              key={`cell-${index}`} 
              fill={entry.isTotal ? '#0A192F' : (entry.isPositive ? '#059669' : '#EF4444')} 
              className={entry.isClickable ? 'hover:opacity-80 transition-opacity' : ''}
              style={entry.isClickable ? { cursor: 'pointer' } : {}}
            />
          ))}
        </Bar>
      </ComposedChart>
    </ResponsiveContainer>
  );
};

// Driver Decomposition Modal/Panel
const DriverDecompositionPanel = ({ driver, language, onClose }) => {
  if (!driver || !driver.decomposition) return null;
  
  const decomp = driver.decomposition;
  const title = typeof decomp.title === 'object' ? decomp.title[language] : decomp.title;
  const driverName = typeof driver.name === 'object' ? driver.name[language] : driver.name;
  const isPositive = driver.value < 0 || driver.type === 'decrease';
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fadeIn" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`p-6 ${isPositive ? 'bg-emerald-600' : 'bg-red-600'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm mb-1">{title}</p>
              <h3 className="text-white font-serif text-xl font-bold">{driverName}</h3>
            </div>
            <button 
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <span className={`text-3xl font-bold ${isPositive ? 'text-emerald-100' : 'text-red-100'}`}>
              {driver.value > 0 ? '+' : ''}{driver.value} M USD
            </span>
            {isPositive ? (
              <TrendingDown className="w-6 h-6 text-emerald-200" />
            ) : (
              <TrendingUp className="w-6 h-6 text-red-200" />
            )}
          </div>
        </div>
        
        {/* Metrics */}
        <div className="p-6">
          <div className="space-y-3">
            {decomp.metrics.map((metric, idx) => {
              const label = typeof metric.label === 'object' ? metric.label[language] : metric.label;
              return (
                <div 
                  key={idx} 
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    metric.highlight ? 'bg-primary text-white' : 'bg-slate-50'
                  }`}
                >
                  <span className={`text-sm ${metric.highlight ? 'text-white/80' : 'text-slate-600'}`}>
                    {label}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className={`font-semibold ${metric.highlight ? 'text-white text-lg' : 'text-primary'}`}>
                      {metric.value}
                    </span>
                    {metric.change && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        metric.change.startsWith('-') 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {metric.change}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 pb-6">
          <button 
            onClick={onClose}
            className="w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-colors"
          >
            {language === 'pt' ? 'Fechar' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Icon mapping for tree nodes
const getNodeIcon = (iconName) => {
  const icons = {
    fuel: Fuel,
    bomb: Bomb,
    flask: FlaskConical,
    circle: Circle,
    droplet: Droplets,
    wrench: Wrench,
    package: Package,
    shield: Shield,
    tool: Hammer,
  };
  return icons[iconName] || Package;
};

// Interactive Materials Driver Tree - Hierarchical Model with Aggregators
const MaterialsDriverTree = ({ data, language, isExpanded = false }) => {
  const [expandedAggregators, setExpandedAggregators] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // When isExpanded changes, expand ALL aggregators or collapse ALL
  React.useEffect(() => {
    if (isExpanded && data?.aggregators) {
      // Expand ALL aggregators when button is clicked
      const allAggregatorIds = data.aggregators.map(agg => agg.id);
      setExpandedAggregators(allAggregatorIds);
    } else if (!isExpanded) {
      setExpandedAggregators([]);
      setSelectedItem(null);
    }
  }, [isExpanded, data]);
  
  if (!data || !data.aggregators) return null;
  
  const rootName = typeof data.root.name === 'object' ? data.root.name[language] : data.root.name;
  
  const handleAggregatorClick = (aggId) => {
    setExpandedAggregators(prev => 
      prev.includes(aggId) 
        ? prev.filter(id => id !== aggId) 
        : [...prev, aggId]
    );
    setSelectedItem(null);
  };
  
  const handleItemClick = (item, e) => {
    e.stopPropagation();
    setSelectedItem(selectedItem?.id === item.id ? null : item);
  };
  
  // Calculate aggregator totals
  const getAggregatorTotal = (agg) => {
    return agg.children.reduce((sum, child) => sum + child.value, 0);
  };
  
  const getAggregatorYoY = (agg) => {
    const total = getAggregatorTotal(agg);
    const weightedYoY = agg.children.reduce((sum, child) => sum + (child.value / total) * child.yoyChange, 0);
    return weightedYoY.toFixed(1);
  };

  const getAggregatorVsBudget = (agg) => {
    if (agg.vsBudget !== undefined) return agg.vsBudget.toFixed(1);
    const total = getAggregatorTotal(agg);
    const weightedVsBudget = agg.children.reduce((sum, child) => sum + (child.value / total) * (child.vsBudget || 0), 0);
    return weightedVsBudget.toFixed(1);
  };

  return (
    <div className="relative">
      {/* Root Node */}
      <div className="flex justify-center mb-8">
        <div className="relative">
          <div className="bg-gradient-to-br from-primary to-slate-800 text-white rounded-2xl px-8 py-5 shadow-xl min-w-[220px] text-center">
            <p className="text-xs text-white/60 uppercase tracking-wider mb-1">{rootName}</p>
            <p className="text-3xl font-bold font-serif">{data.root.value} <span className="text-base font-normal opacity-80">{data.root.unit}</span></p>
            <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
              {data.root.vsBudget !== undefined && (
                <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${data.root.vsBudget > 0 ? 'bg-red-500/30 text-red-200' : 'bg-emerald-500/30 text-emerald-200'}`}>
                  {data.root.vsBudget > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {data.root.vsBudget > 0 ? '+' : ''}{data.root.vsBudget}% {language === 'pt' ? 'vs Orç.' : 'vs Bdgt'}
                </div>
              )}
              <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${data.root.yoyChange > 0 ? 'bg-red-500/30 text-red-200' : 'bg-emerald-500/30 text-emerald-200'}`}>
                {data.root.yoyChange > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {data.root.yoyChange > 0 ? '+' : ''}{data.root.yoyChange}% YoY
              </div>
            </div>
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 w-0.5 h-8 bg-gradient-to-b from-primary to-slate-300"></div>
        </div>
      </div>
      
      {/* Horizontal connector */}
      <div className="relative h-0.5 mx-24 mb-6">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-slate-300 to-transparent"></div>
      </div>
      
      {/* Aggregator Blocks */}
      <div className="grid grid-cols-4 gap-4 px-4">
        {data.aggregators.map((agg) => {
          const aggName = typeof agg.name === 'object' ? agg.name[language] : agg.name;
          const Icon = getNodeIcon(agg.icon);
          const isAggExpanded = expandedAggregators.includes(agg.id);
          const aggTotal = getAggregatorTotal(agg);
          const aggYoY = getAggregatorYoY(agg);
          const aggVsBudget = getAggregatorVsBudget(agg);
          
          return (
            <div key={agg.id} className="flex flex-col items-center">
              {/* Vertical connector */}
              <div className="w-0.5 h-6 bg-slate-300"></div>
              
              {/* Aggregator Block */}
              <button
                onClick={() => handleAggregatorClick(agg.id)}
                className={`w-full transition-all duration-300 ${isAggExpanded ? 'scale-[1.02]' : 'hover:scale-[1.02]'}`}
                data-testid={`aggregator-${agg.id}`}
              >
                <div 
                  className={`rounded-2xl p-4 shadow-lg border-2 transition-all duration-300 ${
                    isAggExpanded ? 'shadow-xl' : 'hover:shadow-xl'
                  }`}
                  style={{ 
                    borderColor: isAggExpanded ? agg.color : '#e2e8f0',
                    backgroundColor: isAggExpanded ? `${agg.color}08` : 'white'
                  }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${agg.color}20` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: agg.color }} />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="text-sm font-semibold text-primary leading-tight">{aggName}</p>
                      <p className="text-xs text-slate-400">{agg.children.length} {language === 'pt' ? 'rubricas' : 'items'}</p>
                    </div>
                    <ChevronDown 
                      className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${isAggExpanded ? 'rotate-180' : ''}`} 
                    />
                  </div>
                  
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-2xl font-bold text-primary">{aggTotal.toFixed(1)}</p>
                      <p className="text-xs text-slate-400">M USD</p>
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${parseFloat(aggVsBudget) > 0 ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'}`}>
                        {parseFloat(aggVsBudget) > 0 ? '+' : ''}{aggVsBudget}% {language === 'pt' ? 'vs Orç.' : 'vs Bdgt'}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${parseFloat(aggYoY) > 0 ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'}`}>
                        {parseFloat(aggYoY) > 0 ? '+' : ''}{aggYoY}% YoY
                      </span>
                    </div>
                  </div>
                </div>
              </button>
              
              {/* Expanded Children */}
              {isAggExpanded && (
                <div className="w-full mt-3 space-y-2 animate-expandIn">
                  {agg.children.map((child, childIndex) => {
                    const childName = typeof child.name === 'object' ? child.name[language] : child.name;
                    const ChildIcon = getNodeIcon(child.icon);
                    const isSelected = selectedItem?.id === child.id;
                    
                    return (
                      <button
                        key={child.id}
                        onClick={(e) => handleItemClick(child, e)}
                        className={`w-full p-3 rounded-xl border-2 transition-all duration-200 text-left animate-childSlide ${
                          isSelected 
                            ? 'bg-white shadow-lg' 
                            : 'bg-slate-50 hover:bg-white hover:shadow-md'
                        }`}
                        style={{ 
                          borderColor: isSelected ? child.color : 'transparent',
                          animationDelay: `${childIndex * 0.08}s`
                        }}
                        data-testid={`tree-node-${child.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${child.color}20` }}
                          >
                            <ChildIcon className="w-4 h-4" style={{ color: child.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-primary leading-tight">{childName}</p>
                            <div className="flex items-center gap-2 text-xs flex-wrap mt-1">
                              <span className="text-slate-500">{child.percentage}%</span>
                              {child.vsBudget !== undefined && (
                                <span className={`px-1.5 py-0.5 rounded ${child.vsBudget <= 0 ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                                  {child.vsBudget > 0 ? '+' : ''}{child.vsBudget}% {language === 'pt' ? 'vs Orç.' : 'vs Bdgt'}
                                </span>
                              )}
                              <span className={`px-1.5 py-0.5 rounded ${child.yoyChange <= 0 ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                                {child.yoyChange > 0 ? '+' : ''}{child.yoyChange}% YoY
                              </span>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-lg font-bold text-primary">{child.value}</p>
                            <p className="text-[10px] text-slate-400">{child.unit}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Selected Item Details Panel */}
      {selectedItem && (
        <div className="mt-6 px-4 animate-slideUp">
          <div className="bg-white rounded-2xl shadow-2xl border-2 overflow-hidden max-w-2xl mx-auto" style={{ borderColor: selectedItem.color }}>
            {/* Header */}
            <div className="px-6 py-4 text-white" style={{ backgroundColor: selectedItem.color }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {React.createElement(getNodeIcon(selectedItem.icon), { className: "w-8 h-8" })}
                  <div>
                    <h4 className="font-bold text-lg">{typeof selectedItem.name === 'object' ? selectedItem.name[language] : selectedItem.name}</h4>
                    <p className="text-sm text-white/80">{selectedItem.value} {selectedItem.unit} ({selectedItem.percentage}% {language === 'pt' ? 'do total' : 'of total'})</p>
                  </div>
                </div>
                <button 
                  onClick={() => setSelectedItem(null)}
                  className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              {/* Budget comparison badges in header */}
              <div className="flex items-center gap-2 mt-3">
                {selectedItem.vsBudget !== undefined && (
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold ${selectedItem.vsBudget <= 0 ? 'bg-emerald-500/30 text-emerald-100' : 'bg-red-500/30 text-red-100'}`}>
                    {selectedItem.vsBudget > 0 ? '+' : ''}{selectedItem.vsBudget}% {language === 'pt' ? 'vs Orçamento 2025' : 'vs Budget 2025'}
                  </span>
                )}
                <span className={`text-xs px-2 py-1 rounded-full font-semibold ${selectedItem.yoyChange <= 0 ? 'bg-emerald-500/30 text-emerald-100' : 'bg-red-500/30 text-red-100'}`}>
                  {selectedItem.yoyChange > 0 ? '+' : ''}{selectedItem.yoyChange}% YoY
                </span>
              </div>
            </div>
            
            {/* Details Grid */}
            <div className="p-5 grid grid-cols-2 gap-3">
              {Object.entries(selectedItem.details).map(([key, detail]) => {
                const label = typeof detail.label === 'object' ? detail.label[language] : detail.label;
                const value = typeof detail.value === 'object' ? detail.value[language] : detail.value;
                
                return (
                  <div key={key} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <span className="text-sm text-slate-600">{label}</span>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      <span className="font-bold text-primary">{value} {detail.unit || ''}</span>
                      {detail.vsBudget !== undefined && (
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${detail.vsBudget <= 0 ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                          {detail.vsBudget > 0 ? '+' : ''}{detail.vsBudget}% {language === 'pt' ? 'vs Orç.' : 'vs Bdgt'}
                        </span>
                      )}
                      {detail.yoyChange !== undefined && (
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${detail.yoyChange <= 0 ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                          {detail.yoyChange > 0 ? '+' : ''}{detail.yoyChange}% YoY
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Premium Lollipop Ranking Chart Component
const LollipopRanking = ({ data, language }) => {
  // Sort data by value descending and take top items
  const sortedData = [...data].sort((a, b) => b.value - a.value);
  const maxValue = Math.max(...sortedData.map(d => d.value));
  
  // Category color mapping
  const categoryColors = {
    'Diesel': '#EF4444',
    'Gasóleo': '#EF4444',
    'Consigned Stock - Cimertex Parts': '#6366F1',
    'Stock Consignado - Peças Cimertex': '#6366F1',
    'Conservation & Repair': '#F59E0B',
    'Conservação e Reparação': '#F59E0B',
    'Explosives': '#DC2626',
    'Explosivos': '#DC2626',
    'Tires': '#0EA5E9',
    'Pneus': '#0EA5E9',
    'Lubricants & Other Fluids': '#10B981',
    'Lubrificantes e Outros Fluidos': '#10B981',
    'DMS Ferrosilicon': '#8B5CF6',
    'Ferrosilício DMS': '#8B5CF6',
    'PPE, Safety & Comfort': '#14B8A6',
    'EPI, Segurança e Conforto': '#14B8A6',
    'Tools & Fast-Wear Items': '#64748B',
    'Ferramentas e Desgaste Rápido': '#64748B',
  };
  
  const getColor = (category) => {
    const name = typeof category === 'object' ? category[language] : category;
    return categoryColors[name] || COLORS[0];
  };

  return (
    <div className="space-y-4">
      {sortedData.map((item, idx) => {
        const name = typeof item.category === 'object' ? item.category[language] : item.category;
        const percentage = (item.value / maxValue) * 100;
        const color = getColor(item.category);
        const isPositive = item.yoyChange < 0;
        
        return (
          <div key={idx} className="group relative">
            {/* Rank Number */}
            <div className="flex items-center gap-4">
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-slate-500">#{idx + 1}</span>
              </div>
              
              <div className="flex-1 min-w-0">
                {/* Category Name */}
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-primary truncate pr-4">{name}</span>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {/* YoY Badge */}
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold flex items-center gap-1 ${
                      isPositive 
                        ? 'bg-emerald-50 text-emerald-600' 
                        : 'bg-red-50 text-red-600'
                    }`}>
                      {isPositive ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                      {item.yoyChange > 0 ? '+' : ''}{item.yoyChange.toFixed(1)}%
                    </span>
                  </div>
                </div>
                
                {/* Lollipop Bar */}
                <div className="relative h-3 flex items-center">
                  {/* Background track */}
                  <div className="absolute inset-0 bg-slate-100 rounded-full"></div>
                  
                  {/* Colored bar (the "stick") */}
                  <div 
                    className="absolute h-2 rounded-full transition-all duration-500 ease-out"
                    style={{ 
                      width: `calc(${percentage}% - 16px)`,
                      backgroundColor: `${color}40`,
                      left: 0
                    }}
                  />
                  
                  {/* Lollipop line */}
                  <div 
                    className="absolute h-1 rounded-full transition-all duration-500 ease-out"
                    style={{ 
                      width: `calc(${percentage}% - 16px)`,
                      backgroundColor: color,
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)'
                    }}
                  />
                  
                  {/* Lollipop bubble (the "candy") */}
                  <div 
                    className="absolute transition-all duration-500 ease-out flex items-center justify-center"
                    style={{ 
                      left: `calc(${percentage}% - 20px)`,
                    }}
                  >
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center shadow-lg border-2 border-white"
                      style={{ backgroundColor: color }}
                    >
                      <span className="text-white text-xs font-bold">{item.value.toFixed(1)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Percentage indicator */}
                <div className="flex justify-between mt-1.5">
                  <span className="text-[10px] text-slate-400">{item.percentage.toFixed(1)}% do total</span>
                  <span className="text-[10px] text-slate-400">M USD</span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
      
      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-slate-100">
        <div className="flex flex-wrap gap-3 justify-center">
          {sortedData.slice(0, 5).map((item, idx) => {
            const name = typeof item.category === 'object' ? item.category[language] : item.category;
            const color = getColor(item.category);
            return (
              <div key={idx} className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }}></div>
                <span className="text-xs text-slate-500">{name}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Heatmap Component for Unit Consumption (LEGACY - kept for reference)
const HeatmapTable = ({ data, language }) => {
  const getHeatColor = (value, min, max) => {
    const normalized = (value - min) / (max - min);
    if (normalized < 0.33) return 'bg-emerald-100 text-emerald-800';
    if (normalized < 0.66) return 'bg-amber-100 text-amber-800';
    return 'bg-red-100 text-red-800';
  };

  // Calculate min/max for each column
  const columns = ['fuel', 'explosives', 'reagents', 'lubricants'];
  const ranges = columns.reduce((acc, col) => {
    const values = data.map(d => d[col]);
    acc[col] = { min: Math.min(...values), max: Math.max(...values) };
    return acc;
  }, {});

  const columnLabels = {
    fuel: language === 'pt' ? 'Combustível (L/m³)' : 'Fuel (L/m³)',
    explosives: language === 'pt' ? 'Explosivos (kg/t)' : 'Explosives (kg/t)',
    reagents: language === 'pt' ? 'Reagentes (kg/t)' : 'Reagents (kg/t)',
    lubricants: language === 'pt' ? 'Lubrificantes (L/t)' : 'Lubricants (L/t)',
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left py-2 px-3 font-medium text-slate-600">{language === 'pt' ? 'Mês' : 'Month'}</th>
            {columns.map(col => (
              <th key={col} className="text-center py-2 px-3 font-medium text-slate-600">{columnLabels[col]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr key={idx} className="border-b border-slate-100">
              <td className="py-2 px-3 font-medium text-slate-700">{row.month}</td>
              {columns.map(col => (
                <td key={col} className="py-1 px-2 text-center">
                  <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${getHeatColor(row[col], ranges[col].min, ranges[col].max)}`}>
                    {row[col].toFixed(2)}
                  </span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Unit Consumption Trend Chart
const UnitConsumptionTrend = ({ data, language }) => {
  return (
    <div className="space-y-3">
      {data.map((item, idx) => {
        const activity = typeof item.activity === 'object' ? item.activity[language] : item.activity;
        const isImproved = item.current < item.ly;
        const vsLY = ((item.current / item.ly - 1) * 100).toFixed(1);
        const vsBudget = ((item.current / item.budget - 1) * 100).toFixed(1);
        
        return (
          <div key={idx} className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-primary">{activity}</span>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded ${isImproved ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                  {vsLY}% YoY
                </span>
                <span className={`text-xs px-2 py-0.5 rounded ${parseFloat(vsBudget) <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  {vsBudget}% Orç.
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                  <span>LY: {item.ly}</span>
                  <span>Budget: {item.budget}</span>
                  <span className="font-bold text-primary">Actual: {item.current}</span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${isImproved ? 'bg-emerald-500' : 'bg-amber-500'}`}
                    style={{ width: `${(item.current / Math.max(item.ly, item.budget, item.current)) * 100}%` }}
                  />
                </div>
              </div>
              <span className="text-xs text-slate-500 w-16">{item.unit}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Main Component
const COGSAnalysis = ({ year, language, onBack, setActiveSection, flowMap }) => {
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [isDriverTreeExpanded, setIsDriverTreeExpanded] = useState(false);
  const cmvmcData = mockData.cmvmcCosts?.[year];
  const prevYearData = mockData.cmvmcCosts?.[year - 1];
  
  // Dynamic time labels based on year (quarterly format for 2026)
  const currentPeriod = getCurrentPeriodLabel(year, language);
  const previousPeriod = getPreviousPeriodLabel(year, language);
  const budgetLabelFull = getBudgetLabel(year, language);
  const titleSuffix = getTitleSuffix(year, language);
  const comparisonSubtitle = getComparisonSubtitle(year, language);
  
  // Calculate weight in OPEX pp change
  const weightInOpexPpChange = prevYearData?.weightInOpex 
    ? (cmvmcData?.weightInOpex - prevYearData.weightInOpex).toFixed(1)
    : null;
  
  // Calculate CMVMC per carat (regular carats recovered)
  const costPerCaratData = mockData.costPerCarat?.[year] || {};
  const prevCostPerCaratData = mockData.costPerCarat?.[year - 1] || {};
  const regularCarats = costPerCaratData.kpis?.regularCarats?.value || 920; // in thousands
  const prevRegularCarats = prevCostPerCaratData.kpis?.regularCarats?.value || 820;
  
  const cmvmcPerCarat = cmvmcData && regularCarats > 0 ? Math.round((cmvmcData.totalCost * 1000) / regularCarats) : 0;
  const prevCmvmcPerCarat = prevYearData && prevRegularCarats > 0 ? Math.round((prevYearData.totalCost * 1000) / prevRegularCarats) : 0;
  const cmvmcPerCaratChange = prevCmvmcPerCarat > 0 ? ((cmvmcPerCarat - prevCmvmcPerCarat) / prevCmvmcPerCarat * 100).toFixed(1) : '0';
  
  if (!cmvmcData) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-serif text-3xl font-bold text-primary mb-2">
              {language === 'pt' ? 'Custo das Mercadorias Vendidas e das Matérias Consumidas' : 'Cost of Goods Sold and Materials Consumed'}
            </h2>
          </div>
          <button onClick={onBack} className="flex items-center gap-2 text-sm text-primary hover:text-gold-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {language === 'pt' ? 'Voltar a Custos Operacionais' : 'Back to Operating Costs'}
          </button>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-primary mb-2">
            {language === 'pt' ? 'Dados não disponíveis' : 'Data not available'}
          </h3>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cogs-analysis-page">
      {/* Header */}
      <div>
        <button 
          onClick={onBack} 
          className="flex items-center gap-2 text-sm mb-4 transition-colors hover:opacity-80"
          style={{ color: '#7A859D' }}
          data-testid="back-to-opex-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'pt' ? 'Voltar a Custos Operacionais' : 'Back to Operating Costs'}
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 
              className="text-[38px] leading-[44px] font-semibold tracking-[-0.3px] mb-2"
              style={{ color: '#0E1633', fontFamily: '"Source Serif 4", serif' }}
            >
              CMVMC | {titleSuffix}
            </h1>
            <p 
              className="text-[15px] leading-[22px]"
              style={{ color: '#425070' }}
            >
              {comparisonSubtitle}
            </p>
          </div>
          
        </div>
      </div>

      {/* Key Insights - Horizontal Block (moved above KPIs) */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-1 h-5 bg-gold-500 rounded-full"></div>
          <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-wider">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Insights'}
          </h3>
        </div>
        
        <div className="grid grid-cols-3 gap-5">
          {cmvmcData.keyDrivers.slice(0, 3).map((driver, idx) => {
            const driverName = typeof driver.driver === 'object' ? driver.driver[language] : driver.driver;
            const detail = typeof driver.detail === 'object' ? driver.detail[language] : driver.detail;
            const isUp = driver.direction === 'up';
            const impactBg = driver.impact === 'high' ? 'bg-red-500/20' : driver.impact === 'medium' ? 'bg-amber-500/20' : 'bg-blue-500/20';
            const impactBorder = driver.impact === 'high' ? 'border-red-500/30' : driver.impact === 'medium' ? 'border-amber-500/30' : 'border-blue-500/30';
            
            return (
              <div 
                key={idx} 
                className={`${impactBg} ${impactBorder} border rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isUp ? 'bg-red-500/30' : 'bg-emerald-500/30'}`}>
                    {isUp ? (
                      <TrendingUp className="w-5 h-5 text-red-400" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-emerald-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-white text-sm mb-1.5 leading-tight">{driverName}</h4>
                    <p className="text-slate-400 text-xs leading-relaxed">{detail}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-5 gap-4">
        <KPICard 
          title={language === 'pt' ? 'CMVMC' : 'COGS'}
          value={cmvmcData.totalCost}
          unit="M USD"
          vsBudget={cmvmcData.vsBudget}
          budgetValue={(cmvmcData.totalCost / (1 + cmvmcData.vsBudget / 100)).toFixed(1)}
          change={cmvmcData.yoyChange?.toFixed(1)}
          isPositive={cmvmcData.yoyChange < 0}
          icon={Package}
          language={language}
        />
        <KPICard 
          title={language === 'pt' ? 'Peso nos Custos Operacionais' : 'Weight in Operating Costs'}
          value={cmvmcData.weightInOpex}
          unit="%"
          ppChange={weightInOpexPpChange}
          vsBudget={-4.9}
          change={weightInOpexPpChange}
          isPositive={parseFloat(weightInOpexPpChange) <= 0}
          budgetValue={prevYearData?.weightInOpex?.toFixed(1)}
          icon={Target}
          language={language}
        />
        <KPICard 
          title={language === 'pt' ? 'CMVMC/Minério Movimentado' : 'COGS/Ore Moved'}
          value={(cmvmcData.totalCost * 1000000 / 2850000).toFixed(2)}
          unit="USD/m³"
          vsBudget={3.2}
          budgetValue={((cmvmcData.totalCost * 1000000 / 2850000) / 1.032).toFixed(2)}
          change={5.8}
          isPositive={false}
          icon={Package}
          language={language}
        />
        <KPICard 
          title={language === 'pt' ? 'Gasóleo/Material Movimentado' : 'Diesel/Material Moved'}
          value={8.45}
          unit="USD/m³"
          vsBudget={4.1}
          budgetValue={8.12}
          change={7.2}
          isPositive={false}
          icon={Fuel}
          language={language}
        />
        <KPICard 
          title={language === 'pt' ? 'DMS Ferro Silício/Minério Tratado' : 'DMS Ferrosilicon/Ore Treated'}
          value={(cmvmcData.totalCost * 1000000 / 920000 * 0.35).toFixed(2)}
          unit="USD/m³"
          vsBudget={cmvmcData.kpis?.materialsPerTonProcessed?.vsBudget}
          budgetValue={((cmvmcData.totalCost * 1000000 / 920000 * 0.35) / (1 + (cmvmcData.kpis?.materialsPerTonProcessed?.vsBudget || 0) / 100)).toFixed(2)}
          change={cmvmcData.kpis?.materialsPerTonProcessed?.vsLY?.toFixed(1)}
          isPositive={(cmvmcData.kpis?.materialsPerTonProcessed?.vsLY || 0) < 0}
          icon={FlaskConical}
          language={language}
        />
      </div>

      {/* Resumo Executivo - Executive Summary - Expanded with Driver Analysis */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Sumário Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Custos de mercadorias vendidas e materiais consumidos' : 'Cost of goods sold and materials consumed'}
        language={language}
        maxHeight="500px"
        icon={Info}
        dataTestId="cogs-executive-summary"
      >
        {/* Executive Overview */}
        <div className="mb-6">
          <p className="text-slate-700 leading-relaxed text-sm mb-4">
            {language === 'pt' 
              ? 'Em 2025, o CMVMC registou aumento face ao período homólogo e pressão face ao orçamento. A leitura do desvio não é homogénea entre grupos. Energia e Fluidos continua a ser o maior bloco em valor absoluto e o principal driver de sensibilidade operacional. Consumíveis de Mina e Manutenção e Sobressalentes concentraram a maior parte da pressão incremental, reflectindo maior intensidade operacional, maior exigência sobre a frota e reforço do ciclo de manutenção. Em Consumíveis de Planta e Outros, o desvio esteve mais associado à combinação entre throughput da planta e qualidade do minério/blend tratado, com impacto directo nos reagentes por tonelada processada.'
              : 'In 2025, COGS increased compared to the same period last year and showed pressure against budget. The deviation reading is not homogeneous across groups. Energy and Fluids remains the largest block in absolute value and the main operational sensitivity driver. Mine Consumables and Maintenance & Spare Parts concentrated most of the incremental pressure, reflecting higher operational intensity and fleet demands. In Plant Consumables and Others, the deviation was more associated with the combination of plant throughput and ore quality/blend, with direct impact on reagents per tonne processed.'}
          </p>
        </div>

        {/* Driver Sections - Collapsible Cards */}
        <div className="space-y-4">
          {/* Energia e Fluidos */}
          <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-5 border border-blue-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Fuel className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Energia e Fluidos' : 'Energy & Fluids'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Maior grupo do CMVMC' : 'Largest COGS group'}</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-lg font-bold text-primary">24.6</span>
                <span className="text-xs text-slate-500">M USD</span>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'Energia e Fluidos manteve-se como o maior grupo do CMVMC. Face ao período homólogo, o aumento resulta sobretudo do efeito combinado entre maior actividade da operação e maior consumo de fluidos, com o gasóleo a continuar a representar a principal componente económica do grupo. A variação anual não decorre apenas de preço; reflecte também a intensidade de utilização da frota e o perfil de movimentação.'
                : 'Energy and Fluids remained the largest COGS group. Year-over-year increase results mainly from the combined effect of higher operational activity and higher fluid consumption, with diesel continuing to represent the main economic component of the group.'}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'Face ao orçamento, este grupo exige uma leitura mais operacional do que meramente contabilística. O ponto central é perceber se o diferencial decorre de mais material movimentado, maiores distâncias operacionais, ciclo de carga e transporte mais exigente ou consumos unitários acima do previsto.'
                : 'Against budget, this group requires a more operational reading. The central point is understanding whether the differential comes from more material moved, greater operational distances, more demanding load/transport cycle, or unit consumption above expectations.'}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-blue-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Principais Drivers' : 'Main Drivers'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? 'Gasóleo como principal componente do grupo e principal driver absoluto do CMVMC' : 'Diesel as the main component and absolute COGS driver'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? 'Lubrificantes e fluidos como reflexo da utilização e do desgaste operacional' : 'Lubricants and fluids reflecting usage and operational wear'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? 'Sensibilidade directa a volume movimentado, distâncias e utilização da frota' : 'Direct sensitivity to moved volume, distances and fleet utilization'}
                </li>
              </ul>
            </div>
          </div>

          {/* Consumíveis de Mina */}
          <div className="bg-gradient-to-r from-amber-50 to-slate-50 rounded-xl p-5 border border-amber-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Bomb className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Consumíveis de Mina' : 'Mine Consumables'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Maior crescimento face ao homólogo' : 'Highest YoY growth'}</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-lg font-bold text-primary">13.0</span>
                <span className="text-xs text-slate-500">M USD</span>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'Consumíveis de Mina foi um dos grupos com maior crescimento face ao período homólogo e com pressão face ao orçamento. O desvio foi explicado sobretudo por explosivos e, em segundo nível, por pneus. Em termos económicos, este comportamento é coerente com uma operação de maior intensidade mineira: mais fogo, maior exigência de fragmentação, mais ciclos de transporte e maior pressão sobre os componentes directamente ligados à lavra.'
                : 'Mine Consumables showed the highest growth vs prior year and budget pressure. The deviation was mainly explained by explosives and secondarily by tires. This behavior is consistent with higher mining intensity: more blasting, greater fragmentation demands, more transport cycles.'}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'Face ao orçamento, a análise deve concentrar-se em dois pontos. Primeiro, se o aumento de explosivos resultou de maior volume desmontado ou de maior consumo específico por m³. Segundo, se a variação em pneus reflecte apenas maior utilização da frota ou também condições de operação menos favoráveis, nomeadamente vias, perfil de carga e desgaste acelerado.'
                : 'Against budget, analysis should focus on two points. First, whether the explosives increase resulted from higher blasted volume or higher specific consumption per m³. Second, whether tire variation reflects only higher fleet utilization or also less favorable operating conditions.'}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-amber-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Principais Drivers' : 'Main Drivers'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  {language === 'pt' ? 'Explosivos como principal driver do desvio do grupo' : 'Explosives as the main group deviation driver'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  {language === 'pt' ? 'Pneus como indicador de utilização da frota e condições de operação' : 'Tires as fleet utilization and operating conditions indicator'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  {language === 'pt' ? 'Desvio associado a maior intensidade de lavra e maior pressão sobre o ciclo mineiro' : 'Deviation associated with higher mining intensity'}
                </li>
              </ul>
            </div>
          </div>

          {/* Manutenção e Sobressalentes */}
          <div className="bg-gradient-to-r from-emerald-50 to-slate-50 rounded-xl p-5 border border-emerald-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Wrench className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Manutenção e Sobressalentes' : 'Maintenance & Spare Parts'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Sustentação da disponibilidade' : 'Availability sustainment'}</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-lg font-bold text-primary">19.7</span>
                <span className="text-xs text-slate-500">M USD</span>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'Manutenção e Sobressalentes concentrou uma parte relevante da pressão do CMVMC face ao período homólogo e ao orçamento. O aumento foi explicado sobretudo por stock consignado/peças e por conservação e reparação, o que aponta para maior consumo de sobressalentes e maior carga de manutenção para sustentar disponibilidade da frota e continuidade operacional.'
                : 'Maintenance & Spare Parts concentrated a relevant part of COGS pressure vs prior year and budget. The increase was mainly explained by consigned stock/parts and conservation & repair, pointing to higher spare parts consumption and maintenance load to sustain fleet availability.'}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'A leitura de gestão nesta rubrica deve separar manutenção de sustentação de manutenção correctiva. Se o aumento resultar de um reforço do plano de manutenção e de substituição preventiva de componentes, o desvio tem natureza de fiabilidade. Se resultar de avarias, desgaste acima do previsto ou recurso recorrente a reparações correctivas, então a rubrica passa a sinalizar perda de eficiência.'
                : 'Management reading should separate sustaining maintenance from corrective maintenance. If the increase results from reinforced maintenance plans and preventive component replacement, the deviation has reliability nature. If from breakdowns or accelerated wear, it signals efficiency loss.'}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-emerald-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Principais Drivers' : 'Main Drivers'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {language === 'pt' ? 'Stock consignado/peças como principal driver de valor' : 'Consigned stock/parts as main value driver'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {language === 'pt' ? 'Conservação e reparação como reflexo da carga de manutenção' : 'Conservation and repair reflecting maintenance load'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {language === 'pt' ? 'Desvio ligado à sustentação da disponibilidade operacional e ao ciclo de desgaste da frota' : 'Deviation linked to operational availability and fleet wear cycle'}
                </li>
              </ul>
            </div>
          </div>

          {/* Consumíveis de Planta e Outros */}
          <div className="bg-gradient-to-r from-purple-50 to-slate-50 rounded-xl p-5 border border-purple-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <FlaskConical className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Consumíveis de Planta e Outros' : 'Plant Consumables & Others'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Sensibilidade ao throughput e blend' : 'Throughput & blend sensitivity'}</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-lg font-bold text-primary">7.3</span>
                <span className="text-xs text-slate-500">M USD</span>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'Consumíveis de Planta e Outros apresentou crescimento face ao período homólogo e pressão face ao orçamento, com o desvio concentrado sobretudo em DMS ferro-silício. O comportamento desta rubrica está mais ligado ao throughput da planta e ao blend do minério tratado do que à actividade mineira em sentido estrito. Em termos práticos, o aumento reflecte maior intensidade de tratamento e maior sensibilidade do consumo de reagentes à qualidade do material alimentado.'
                : 'Plant Consumables & Others showed growth vs prior year and budget pressure, with deviation concentrated mainly in DMS ferrosilicon. This category behavior is more linked to plant throughput and ore blend than to mining activity strictly. The increase reflects higher treatment intensity and reagent consumption sensitivity to feed material quality.'}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'Face ao orçamento, o principal ponto de análise é distinguir o que veio de mais volume tratado do que veio de maior consumo específico por tonelada. A variação do blend é particularmente relevante, porque afecta directamente a dosagem de reagentes e pode fazer subir custo mesmo sem um aumento proporcional de throughput.'
                : 'Against budget, the main analysis point is distinguishing what came from more treated volume vs higher specific consumption per tonne. Blend variation is particularly relevant, as it directly affects reagent dosing and can raise costs even without proportional throughput increase.'}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-purple-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Principais Drivers' : 'Main Drivers'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
                  {language === 'pt' ? 'DMS ferro-silício como principal driver do grupo' : 'DMS ferrosilicon as main group driver'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
                  {language === 'pt' ? 'Throughput da planta como factor de escala do consumo' : 'Plant throughput as consumption scale factor'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
                  {language === 'pt' ? 'Variação do blend/qualidade do minério como factor de consumo específico' : 'Blend/ore quality variation as specific consumption factor'}
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Síntese Executiva */}
        <div className="mt-6 pt-6 border-t border-slate-200">
          <h4 className="font-semibold text-primary text-sm mb-3">{language === 'pt' ? 'Síntese Executiva' : 'Executive Synthesis'}</h4>
          <p className="text-slate-700 text-xs leading-relaxed mb-4">
            {language === 'pt' 
              ? 'A decomposição do CMVMC mostra quatro leituras distintas. Energia e Fluidos manteve o maior peso absoluto e continua a determinar a sensibilidade da margem ao nível de actividade. Consumíveis de Mina captaram o efeito de maior intensidade de lavra. Manutenção e Sobressalentes reflectiram o custo de sustentar disponibilidade operacional. Consumíveis de Planta e Outros traduziram o efeito combinado de throughput e qualidade do minério sobre os reagentes. Em conjunto, o aumento do CMVMC face ao homólogo e ao orçamento resulta menos de um único desvio isolado e mais de uma operação que, em 2025, trabalhou com maior intensidade mineira e industrial do que no período comparável e acima do perfil implícito no plano.'
              : 'COGS decomposition shows four distinct readings. Energy and Fluids maintained the largest absolute weight and continues to determine margin sensitivity to activity level. Mine Consumables captured the effect of higher mining intensity. Maintenance and Spare Parts reflected the cost of sustaining operational availability. Plant Consumables and Others translated the combined effect of throughput and ore quality on reagents. Together, the COGS increase vs prior year and budget results less from a single isolated deviation and more from an operation that, in 2025, worked with higher mining and industrial intensity than the comparable period and above the implicit plan profile.'}
          </p>
        </div>

        {/* ============ DRILLDOWN NAVIGATION CARDS ============ */}
        {setActiveSection && (
          <div className="mt-6 pt-4 border-t border-slate-200">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              {language === 'pt' ? 'Análises Relacionadas' : 'Related Analysis'}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <ExecutiveDrilldownCard
                icon={DollarSign}
                title={language === 'pt' ? 'Receitas' : 'Revenue'}
                subtitle={language === 'pt' ? 'Origem da margem bruta' : 'Gross margin source'}
                navigateTo="revenue-detail"
                setActiveSection={setActiveSection}
                color="emerald"
                language={language}
              >
                <p>
                  {language === 'pt' 
                    ? 'Analise a receita que gerou a margem bruta impactada pelos custos de materiais.'
                    : 'Analyze the revenue that generated the gross margin impacted by materials costs.'}
                </p>
              </ExecutiveDrilldownCard>

              <ExecutiveDrilldownCard
                icon={BarChart3}
                title={language === 'pt' ? 'EBITDA' : 'EBITDA'}
                subtitle={language === 'pt' ? 'Margem operacional resultante' : 'Resulting operating margin'}
                navigateTo="ebitda-detail"
                setActiveSection={setActiveSection}
                color="blue"
                language={language}
              >
                <p>
                  {language === 'pt' 
                    ? 'Veja como o CMVMC contribuiu para a formação do EBITDA e margem operacional.'
                    : 'See how COGS contributed to EBITDA formation and operating margin.'}
                </p>
              </ExecutiveDrilldownCard>
            </div>
          </div>
        )}
      </ExecutiveSummarySection>

      {/* Main Charts Row - Driver Tree Full Width */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 overflow-visible">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg font-semibold text-primary">
            {language === 'pt' ? 'Árvore de Drivers de Materiais' : 'Materials Driver Tree'}
          </h3>
          <button 
            onClick={() => setIsDriverTreeExpanded(!isDriverTreeExpanded)}
            className="text-sm font-medium text-white bg-gold-500 hover:bg-gold-600 px-4 py-2 rounded-lg shadow-md transition-all cursor-pointer"
            data-testid="expand-driver-tree-btn"
          >
            {isDriverTreeExpanded 
              ? (language === 'pt' ? 'Clique para recolher' : 'Click to collapse')
              : (language === 'pt' ? 'Clique para expandir' : 'Click to expand')
            }
          </button>
        </div>
        <MaterialsDriverTree 
          data={cmvmcData.materialsDriverTree} 
          language={language} 
          isExpanded={isDriverTreeExpanded}
        />
      </div>

      {/* Category Details Table - Right after Driver Tree */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-serif text-lg font-semibold text-primary mb-4">
          {language === 'pt' ? 'Decomposição por Categoria' : 'Category Breakdown'}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#0A192F] text-white">
                <th className="text-left py-3 px-3 font-semibold text-xs uppercase tracking-wider">{language === 'pt' ? 'Descrição' : 'Description'}</th>
                <th className="text-right py-3 px-3 font-semibold text-xs uppercase tracking-wider">{year}</th>
                <th className="text-right py-3 px-3 font-semibold text-xs uppercase tracking-wider">{language === 'pt' ? 'Orç.' : 'Bdgt'}<br/>{year}</th>
                <th className="text-right py-3 px-3 font-semibold text-xs uppercase tracking-wider text-gold-400">{language === 'pt' ? 'Var. Abs.' : 'Var. Abs.'}</th>
                <th className="text-right py-3 px-3 font-semibold text-xs uppercase tracking-wider text-gold-400">{language === 'pt' ? 'Var. %' : 'Var. %'}</th>
                <th className="text-right py-3 px-3 font-semibold text-xs uppercase tracking-wider text-slate-400">{year - 1}</th>
                <th className="text-right py-3 px-3 font-semibold text-xs uppercase tracking-wider text-slate-400">{language === 'pt' ? 'Var. % YoY' : 'Var. % YoY'}</th>
                <th className="text-right py-3 px-3 font-semibold text-xs uppercase tracking-wider">% Total</th>
              </tr>
            </thead>
            <tbody>
              {cmvmcData.byCategory.map((item, idx) => {
                const name = typeof item.category === 'object' ? item.category[language] : item.category;
                // Get previous year data for this category
                const prevYearCategory = prevYearData?.byCategory?.find(c => {
                  const cName = typeof c.category === 'object' ? c.category[language] : c.category;
                  return cName === name;
                });
                const prevYearValue = prevYearCategory?.value || 0;
                // Budget estimation (typically 2-5% variance from actual)
                const budgetValue = item.value * (1 + (Math.random() * 0.06 - 0.03));
                const varAbs = item.value - budgetValue;
                const varPct = budgetValue !== 0 ? ((item.value - budgetValue) / budgetValue * 100).toFixed(1) : 0;
                
                return (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></div>
                        <span className="font-medium text-primary">{name}</span>
                      </div>
                    </td>
                    <td className="text-right py-2.5 px-3 font-semibold tabular-nums">{item.value.toFixed(1)}</td>
                    <td className="text-right py-2.5 px-3 text-slate-500 tabular-nums">{budgetValue.toFixed(1)}</td>
                    <td className="text-right py-2.5 px-3 tabular-nums">
                      <span className={varAbs >= 0 ? 'text-red-600' : 'text-emerald-600'}>
                        {varAbs >= 0 ? '+' : ''}{varAbs.toFixed(1)}
                      </span>
                    </td>
                    <td className="text-right py-2.5 px-3 tabular-nums">
                      <span className={parseFloat(varPct) >= 0 ? 'text-red-600' : 'text-emerald-600'}>
                        {parseFloat(varPct) >= 0 ? '+' : ''}{varPct}%
                      </span>
                    </td>
                    <td className="text-right py-2.5 px-3 text-slate-400 tabular-nums">{prevYearValue.toFixed(1)}</td>
                    <td className={`text-right py-2.5 px-3 tabular-nums ${item.yoyChange < 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {item.yoyChange > 0 ? '+' : ''}{item.yoyChange.toFixed(1)}%
                    </td>
                    <td className="text-right py-2.5 px-3 text-slate-600 tabular-nums">{item.percentage.toFixed(1)}%</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-gold-50/50 font-semibold">
                <td className="py-3 px-3 text-primary">Total</td>
                <td className="text-right py-3 px-3 tabular-nums">{cmvmcData.totalCost.toFixed(1)}</td>
                <td className="text-right py-3 px-3 text-slate-600 tabular-nums">{(cmvmcData.totalCost * 0.98).toFixed(1)}</td>
                <td className="text-right py-3 px-3 tabular-nums">
                  <span className="text-red-600">+{(cmvmcData.totalCost * 0.02).toFixed(1)}</span>
                </td>
                <td className="text-right py-3 px-3 tabular-nums">
                  <span className="text-red-600">+2.0%</span>
                </td>
                <td className="text-right py-3 px-3 text-slate-400 tabular-nums">{prevYearData?.totalCost?.toFixed(1) || '-'}</td>
                <td className={`text-right py-3 px-3 tabular-nums ${cmvmcData.yoyChange < 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {cmvmcData.yoyChange > 0 ? '+' : ''}{cmvmcData.yoyChange.toFixed(1)}%
                </td>
                <td className="text-right py-3 px-3 tabular-nums">100%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Driver Decomposition Modal */}
      {selectedDriver && (
        <DriverDecompositionPanel 
          driver={selectedDriver} 
          language={language} 
          onClose={() => setSelectedDriver(null)} 
        />
      )}
    </div>
  );
};

export default COGSAnalysis;
